import React, { useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

type Props = {
  code: string;
  language?: string;
  title?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
};

/**
 * コードブロック（折りたたみ対応）
 * ダーク背景で固定し、どのカラーモードでも視認性を確保する。
 */
const WorkflowCodeBlock: React.FC<Props> = ({
  code,
  language = 'text',
  title,
  collapsible = false,
  defaultCollapsed = true,
}) => {
  const [collapsed, setCollapsed] = useState(collapsible && defaultCollapsed);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: 1,
        borderColor: '#2d333b',
      }}
    >
      {/* ヘッダー — 常にダーク */}
      {(title || collapsible) && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 1.5,
            py: 0.75,
            bgcolor: '#1c2128',
            borderBottom: collapsed ? 0 : 1,
            borderColor: '#2d333b',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {title && (
              <Typography variant="caption" fontWeight={600} sx={{ color: '#90caf9' }}>
                {title}
              </Typography>
            )}
            {language && (
              <Typography variant="caption" sx={{ color: '#768390' }}>
                {language}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5, color: '#768390' }}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
            {collapsible && (
              <Button
                size="small"
                onClick={() => setCollapsed(!collapsed)}
                endIcon={collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.7rem',
                  minWidth: 0,
                  px: 1,
                  color: '#768390',
                }}
              >
                {collapsed ? '展開' : '折りたたむ'}
              </Button>
            )}
          </Stack>
        </Stack>
      )}

      {/* コード本体 — 常にダーク */}
      {!collapsed && (
        <Box
          sx={{
            p: 1.5,
            overflow: 'auto',
            bgcolor: '#0d1117',
            color: '#e6edf3',
            fontSize: '0.78rem',
            lineHeight: 1.6,
            fontFamily: '"JetBrains Mono", "Fira Code", "SFMono-Regular", Consolas, monospace',
            whiteSpace: 'pre',
            m: 0,
          }}
        >
          {code}
        </Box>
      )}
    </Box>
  );
};

export default WorkflowCodeBlock;
